// "Achter de schermen": a log for partners of what the technology does at each moment.

import { api, type NameResult } from './api.js';
import { $, el, escapeHtml } from './util.js';

export type Tag = 'verhaal' | 'ai' | 'sensor' | 'privacy' | 'illusie';
const TAG_LABEL: Record<Tag, string> = { verhaal: 'verhaal', ai: 'AI', sensor: 'sensor', privacy: 'privacy', illusie: 'illusie' };

const REASON_LABEL: Record<string, string> = {
  name: 'naam', uncertain: 'twijfel', gibberish: 'toetsenbordgebrabbel', word: 'gewoon woord', fictional: 'bekende of verzonnen naam',
  offensive: 'scheldwoord', refusal: 'weigert', question: 'stelt een vraag', sentence: 'een hele zin', too_short: 'te kort',
  digits: 'bevat cijfers', empty: 'leeg', character_ellen: 'naam van een personage', character_joris: 'naam van een personage',
};
const VERDICT_LABEL = { accept: 'aanvaard', confirm: 'navragen', reject: 'afgewezen' } as const;

let started = performance.now();

export const backstage = {
  reset() {
    started = performance.now();
    $('#backstage-log').replaceChildren();
  },

  log(tag: Tag, title: string, detail = '', extra?: HTMLElement) {
    const t = (performance.now() - started) / 1000;
    const stamp = `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
    const item = el('li.logitem', {},
      el('div.logitem__top', {}, el(`span.tag.tag--${tag}`, {}, TAG_LABEL[tag]), stamp),
      el('div.logitem__title', {}, title),
      detail ? el('div.logitem__detail', { html: detail }) : null,
      extra ?? null,
    );
    const list = $('#backstage-log');
    list.append(item);
    list.scrollTop = list.scrollHeight;
  },

  /** Renders a name-model result: verdict, reason and class probabilities per token. */
  nameResult(r: NameResult): HTMLElement {
    const wrap = el('div', {});
    wrap.append(el(`span.verdict.verdict--${r.verdict}`, {}, VERDICT_LABEL[r.verdict]), REASON_LABEL[r.reason] ?? r.reason);
    if (r.extractedFrom) wrap.append(el('div.small', { html: `uit de zin gehaald: <code>${escapeHtml(r.name ?? '')}</code>` }));
    if (r.stage === 'rules') wrap.append(el('div.small', { html: `beslist door een regel (${escapeHtml(r.rule ?? '')}), het model kwam niet aan bod` }));
    for (const t of r.tokens.filter((x) => !x.skipped).slice(0, 3)) {
      const bars = el('div.bars', {});
      const rows: Array<[string, number, string]> = [['naam', t.probs.name, 'var(--ok)'], ['gebrabbel', t.probs.gibberish, 'var(--alarm)'], ['woord', t.probs.word, '#ffd27a']];
      bars.append(el('span', { style: 'grid-column: 1 / -1; color: var(--text)' }, `“${t.token}”${t.lexicon ? ` · staat op de lijst: ${t.lexicon === 'name' ? 'naam' : 'woord'}` : ''}`));
      for (const [label, v, c] of rows) bars.append(el('span', {}, label), el('i', { style: `--v:${(v * 100).toFixed(0)}%; --c:${c}` }), el('span', {}, `${Math.round(v * 100)}%`));
      wrap.append(bars);
    }
    wrap.append(el('div.small', { style: 'margin-top:6px' }, `zekerheid ${Math.round(r.confidence * 100)}% · ${r.ms} ms op de server`));
    return wrap;
  },

  async initModelBox() {
    try {
      const m = await api.model();
      $('#model-info').innerHTML = `Klein model, zelf getraind bij het bouwen: softmax-regressie op letter-n-grammen. ${m.trainSamples + m.validationSamples} voorbeelden (namen, woorden, gebrabbel) · <strong>${Math.round(m.validationAccuracy * 100)}%</strong> juist op ${m.validationSamples} niet-geziene voorbeelden · ${m.sizeKb} kB · geen externe AI-dienst.`;
    } catch { $('#model-info').textContent = 'Model niet bereikbaar.'; }
    const input = $<HTMLInputElement>('#model-input');
    const out = $('#model-result');
    let timer = 0;
    const run = async () => {
      const text = input.value.trim();
      if (!text) { out.replaceChildren(); return; }
      try { out.replaceChildren(this.nameResult(await api.name(text, 1))); } catch { out.textContent = 'Geen verbinding met de server.'; }
    };
    input.addEventListener('input', () => { clearTimeout(timer); timer = window.setTimeout(run, 220); });
    $('#model-form').addEventListener('submit', (e) => { e.preventDefault(); void run(); });
  },
};
